import {
  AUTHORED_FONT_FAMILIES,
  AUTHORED_FONT_WEIGHTS,
  type AuthoredFontFamilyId,
} from '../typography/fonts';
import type { Annotation, AnnotationTextStyle } from './model';

export type AnnotationFontFamilyId = AuthoredFontFamilyId;
export const ANNOTATION_FONT_WEIGHTS = AUTHORED_FONT_WEIGHTS;
export type AnnotationFontWeight = (typeof ANNOTATION_FONT_WEIGHTS)[number];
export const ANNOTATION_FONT_FAMILIES = AUTHORED_FONT_FAMILIES;
export const ANNOTATION_HALOS = [
  { label: 'Off', width: 0 },
  { label: 'Subtle', width: 0.75 },
  { label: 'Normal', width: 1.5 },
  { label: 'Strong', width: 2.25 },
] as const;

export function isAnnotationFontFamily(value: unknown): value is AnnotationFontFamilyId {
  return ANNOTATION_FONT_FAMILIES.some((font) => font.id === value);
}
export function annotationTypography(style: AnnotationTextStyle) {
  return {
    fontFamily: style.fontFamily,
    fontWeight: style.fontWeight ?? 400,
    fontStyle: style.fontStyle ?? 'normal',
  };
}

export function hasModernAnnotationStyle(style: AnnotationTextStyle): boolean {
  return (
    style.fontWeight !== undefined ||
    style.fontStyle !== undefined ||
    style.textHaloWidth !== undefined ||
    style.boxHeight !== undefined ||
    ['manrope', 'ibm-plex-sans', 'dm-sans'].includes(style.fontFamily)
  );
}

export const ANNOTATION_PRESETS = [
  { id: 'framed', label: 'Framed' },
  { id: 'halo', label: 'Halo' },
  { id: 'dark-accent', label: 'Dark Accent' },
] as const;
export type AnnotationPresetId = (typeof ANNOTATION_PRESETS)[number]['id'];
type TextAnnotation = Extract<Annotation, { type: 'text' | 'callout' }>;

export function annotationPresetStyle(id: AnnotationPresetId): AnnotationTextStyle {
  return {
    fontFamily: 'manrope',
    fontWeight: 600,
    fontStyle: 'normal',
    fontSize: 15,
    colour: id === 'dark-accent' ? '#ffffff' : '#334155',
    alignment: 'left',
    backgroundColour: id === 'dark-accent' ? '#143b33' : '#ffffff',
    backgroundOpacity: id === 'halo' ? 0 : 0.96,
    borderColour: id === 'dark-accent' ? '#31584f' : '#c4cad3',
    borderWidth: id === 'halo' ? 0 : 1,
    padding: id === 'halo' ? 3 : 9,
    boxWidth: 160,
    cornerRadius: 6,
    textHaloWidth: id === 'halo' ? 1.5 : 0,
    textHaloColour: '#ffffff',
  };
}

/** One-time recipe. Geometry, elevation, content, identity and unrelated preferences survive. */
export function applyAnnotationPreset<T extends TextAnnotation>(
  annotation: T,
  id: AnnotationPresetId,
): T {
  const { boxHeight: _boxHeight, ...style } = annotation.style;
  void _boxHeight;
  return {
    ...annotation,
    style: {
      ...style,
      ...annotationPresetStyle(id),
      alignment: annotation.style.alignment,
      ...(annotation.type === 'callout'
        ? {
            leaderColour: id === 'dark-accent' ? '#31584f' : '#475569',
            leaderStyle: 'tapered' as const,
            leaderWidth: 3,
            leaderOpacity: 0.8,
          }
        : {}),
    },
  };
}

export function matchingAnnotationPreset(annotation: TextAnnotation): AnnotationPresetId | null {
  if (annotation.style.boxHeight !== undefined) return null;
  return (
    ANNOTATION_PRESETS.find(({ id }) => {
      const recipe = applyAnnotationPreset(annotation, id).style;
      return Object.entries(recipe).every(
        ([key, value]) => annotation.style[key as keyof typeof annotation.style] === value,
      );
    })?.id ?? null
  );
}
